/**
 * CustomerLibrary — unified content access (replaces old My Courses).
 *
 * Sources its data from /api/portal/library which already normalises every
 * purchase's delivery payload into a flat item list. Categories: all /
 * videos / downloads / replays / memberships / resources.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import {
  BookOpen, Search, PlayCircle, FileText, Download, ExternalLink, Link as LinkIcon,
  Video, Loader2, Filter, FolderOpen,
} from "lucide-react";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "videos", label: "Videos", icon: Video },
  { id: "downloads", label: "Downloads", icon: Download },
  { id: "replays", label: "Replays", icon: PlayCircle },
  { id: "memberships", label: "Memberships" },
  { id: "resources", label: "Resources" },
];

function iconFor(kind) {
  if (kind === "pdf") return FileText;
  if (kind === "youtube" || kind === "vimeo" || kind === "webinar") return PlayCircle;
  if (kind === "drive" || kind === "file") return Download;
  if (kind === "membership") return LinkIcon;
  return ExternalLink;
}

function thumbBg(item) {
  if (item.thumbnail_url) return { backgroundImage: `url(${item.thumbnail_url})`, backgroundSize: "cover", backgroundPosition: "center" };
  // Deterministic gradient based on title hash
  const hue = Math.abs((item.title || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0)) % 360;
  return { background: `linear-gradient(135deg, hsl(${hue} 70% 65%), hsl(${(hue + 60) % 360} 70% 55%))` };
}

function ItemCard({ item }) {
  const Icon = iconFor(item.kind);
  return (
    <a
      href={item.url || "#"}
      target={item.url ? "_blank" : undefined}
      rel="noreferrer"
      className="surface overflow-hidden hover:shadow-md transition group"
      data-testid={`library-item-${item.item_id}`}
    >
      <div className="h-32 grid place-items-center text-white/90 relative" style={thumbBg(item)}>
        <Icon className="h-10 w-10 drop-shadow-lg" />
        <span className="absolute top-2 right-2 pill bg-white/30 text-white text-[10px] backdrop-blur-sm">{item.kind}</span>
      </div>
      <div className="p-4">
        <p className="font-semibold text-ink truncate">{item.title}</p>
        <p className="text-[11px] text-meta mt-0.5 truncate">
          {(item.asset_type || "").replace("_", " ")}{item.created_at && ` · purchased ${new Date(item.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short" })}`}
        </p>
        {item.url && (
          <p className="mt-2 text-[11px] text-ai font-semibold inline-flex items-center gap-1 group-hover:underline">
            Open <ExternalLink className="h-3 w-3" />
          </p>
        )}
      </div>
    </a>
  );
}

export default function CustomerLibrary() {
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");

  useEffect(() => {
    api.get("/portal/library").then((r) => setItems(r.data?.items || [])).catch(() => setItems([]));
  }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = search.trim().toLowerCase();
    return items
      .filter((it) => cat === "all" || it.category === cat)
      .filter((it) => !q || (it.title || "").toLowerCase().includes(q));
  }, [items, search, cat]);

  if (items === null) {
    return <div className="text-meta py-10 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading library…</div>;
  }

  return (
    <div data-testid="customer-library">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-ink inline-flex items-center gap-2"><BookOpen className="h-6 w-6 text-ai" /> Library</h1>
        <p className="text-sm text-meta mt-1">Everything you've unlocked — videos, downloads, replays, and resources in one place.</p>
      </div>

      {items.length === 0 ? (
        <div className="surface p-10 text-center" data-testid="library-empty">
          <FolderOpen className="h-8 w-8 text-zinc-300 mx-auto" />
          <p className="mt-3 font-display text-lg font-bold text-ink">Your library is empty</p>
          <p className="mt-1 text-sm text-meta max-w-md mx-auto">When a creator delivers a video, PDF, download, or replay to you, it shows up here for one-click access.</p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="library-filters">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-3 top-2.5 text-meta" />
              <input
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search your library"
                data-testid="library-search"
                className="rounded-full border border-line bg-white pl-9 pr-4 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div className="flex flex-wrap gap-1">
              {CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCat(c.id)}
                  data-testid={`library-cat-${c.id}`}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${cat === c.id ? "ashra-gradient-bg text-white shadow-sm" : "bg-white border border-line text-ink-soft hover:border-ai hover:text-ai"}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <span className="ml-auto text-xs text-meta inline-flex items-center gap-1">
              <Filter className="h-3 w-3" /> {filtered.length} of {items.length}
            </span>
          </div>

          {filtered.length === 0 ? (
            <div className="surface p-8 text-center text-sm text-meta">No items match.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="library-grid">
              {filtered.map((it) => <ItemCard key={it.item_id} item={it} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
